// Shared corner-text stamping primitive used by the watermark (brand, free
// tier) and the AI-Act badge (transparency, all AI output). Both previously
// duplicated the clamp + save/restore + font/align logic; this is the single
// source of truth. Pure; works against a minimal structural canvas context so
// it stays unit-testable without a real <canvas>.

export interface StampCtx {
  save(): void;
  restore(): void;
  fillText(text: string, x: number, y: number): void;
  measureText(text: string): { width: number };
  font: string;
  fillStyle: string | CanvasGradient | CanvasPattern;
  textBaseline: CanvasTextBaseline;
  textAlign: CanvasTextAlign;
  globalAlpha: number;
}

export type Corner = 'tl' | 'tr' | 'bl' | 'br';

export interface CornerStampOptions {
  text: string;
  corner: Corner;
  /** Fraction of the shorter side used as font size. */
  fontScale: number;
  /** Opacity 0..1. */
  opacity: number;
  /** Padding in px from the corner. */
  padding: number;
  /** Font size clamp [min, max] in px. */
  minFont: number;
  maxFont: number;
  fillStyle?: string;
}

function alignFor(corner: Corner): {
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
} {
  const left = corner === 'tl' || corner === 'bl';
  const top = corner === 'tl' || corner === 'tr';
  return { textAlign: left ? 'left' : 'right', textBaseline: top ? 'top' : 'bottom' };
}

function positionFor(corner: Corner, width: number, height: number, padding: number) {
  const left = corner === 'tl' || corner === 'bl';
  const top = corner === 'tl' || corner === 'tr';
  return { x: left ? padding : width - padding, y: top ? padding : height - padding };
}

/**
 * Stamp text in a corner. Saves/restores so the caller's draw state is
 * untouched. Returns the font px actually used (after clamping).
 */
export function stampCornerText(
  ctx: StampCtx,
  width: number,
  height: number,
  opts: CornerStampOptions,
): number {
  const fontPx = Math.max(
    opts.minFont,
    Math.min(opts.maxFont, Math.round(Math.min(width, height) * opts.fontScale)),
  );
  const { textAlign, textBaseline } = alignFor(opts.corner);
  const { x, y } = positionFor(opts.corner, width, height, opts.padding);

  ctx.save();
  ctx.globalAlpha = opts.opacity;
  ctx.font = `600 ${fontPx}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = opts.fillStyle ?? 'rgba(255,255,255,0.9)';
  ctx.textBaseline = textBaseline;
  ctx.textAlign = textAlign;
  ctx.fillText(opts.text, x, y);
  ctx.restore();
  return fontPx;
}
